/**
 * Cliente de API para Inventario, Almacenes y Kardex.
 */

import { apiRequest } from "./api";

export interface WarehousePublic {
  id: string;
  branch_id: string;
  code: string;
  name: string;
  address?: string | null;
  is_default: boolean;
  is_active: boolean;
}

export interface WarehouseCreate {
  branch_id: string;
  code: string;
  name: string;
  address?: string | null;
  is_default?: boolean;
}

export interface BalancePublic {
  warehouse_id: string;
  variant_id: string;
  quantity: string | number;
  avg_cost_cents: number;
  last_movement_at: string | null;
  is_negative: boolean;
  warehouse_name?: string | null;
  sku?: string | null;
  product_name?: string | null;
  variant_name?: string | null;
}

export interface BalanceList {
  items: BalancePublic[];
  total: number;
}

export interface MovementPublic {
  id: string;
  warehouse_id: string;
  variant_id: string;
  kind: "inicial" | "entrada" | "salida" | "ajuste" | "traslado_entrada" | "traslado_salida" | string;
  quantity: string | number;
  unit_cost_cents: number;
  total_cost_cents: number;
  reference_type?: string | null;
  reference_id?: string | null;
  reason?: string | null;
  occurred_at: string;
  warehouse_name?: string | null;
  sku?: string | null;
  product_name?: string | null;
  variant_name?: string | null;
}

export interface MovementList {
  items: MovementPublic[];
  total: number;
}

export interface StockEntry {
  warehouse_id: string;
  variant_id: string;
  quantity: number | string;
  unit_cost_cents?: number | null;
  kind: "entrada" | "salida";
  reason?: string | null;
}

export interface StockAdjustment {
  warehouse_id: string;
  variant_id: string;
  counted_quantity: number | string;
  reason?: string | null;
  unit_cost_cents?: number | null;
}

export interface StockTransfer {
  from_warehouse_id: string;
  to_warehouse_id: string;
  variant_id: string;
  quantity: number | string;
  reason?: string | null;
}

export interface IntegrityCheck {
  warehouse_id: string;
  variant_id: string;
  stored_quantity: string | number;
  kardex_quantity: string | number;
  stored_avg_cost_cents: number;
  kardex_avg_cost_cents: number;
  is_consistent: boolean;
  explanation: string;
}

export function listWarehouses(branchId?: string, onlyActive = false): Promise<WarehousePublic[]> {
  const params = new URLSearchParams();
  if (branchId) params.set("branch_id", branchId);
  if (onlyActive) params.set("only_active", "true");
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiRequest<WarehousePublic[]>(`/api/v1/warehouses${q}`);
}

export function createWarehouse(payload: WarehouseCreate): Promise<WarehousePublic> {
  return apiRequest<WarehousePublic>("/api/v1/warehouses", {
    method: "POST",
    body: payload,
  });
}

export function listBalances(params?: {
  warehouse_id?: string;
  variant_id?: string;
  q?: string;
  only_negative?: boolean;
  limit?: number;
  offset?: number;
}): Promise<BalanceList> {
  const query = new URLSearchParams();
  if (params?.warehouse_id) query.set("warehouse_id", params.warehouse_id);
  if (params?.variant_id) query.set("variant_id", params.variant_id);
  if (params?.q) query.set("q", params.q);
  if (params?.only_negative) query.set("only_negative", "true");
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  const q = query.toString() ? `?${query.toString()}` : "";
  return apiRequest<BalanceList>(`/api/v1/stock/balances${q}`);
}

export function listMoves(params?: {
  warehouse_id?: string;
  variant_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<MovementList> {
  const query = new URLSearchParams();
  if (params?.warehouse_id) query.set("warehouse_id", params.warehouse_id);
  if (params?.variant_id) query.set("variant_id", params.variant_id);
  if (params?.q) query.set("q", params.q);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  const q = query.toString() ? `?${query.toString()}` : "";
  return apiRequest<MovementList>(`/api/v1/stock/moves${q}`);
}

export function recordMovement(payload: StockEntry) {
  return apiRequest<{ move: MovementPublic; balance: BalancePublic }>("/api/v1/stock/movements", {
    method: "POST",
    body: payload,
  });
}

export function adjustStock(payload: StockAdjustment) {
  return apiRequest<{ move: MovementPublic; balance: BalancePublic }>("/api/v1/stock/adjustments", {
    method: "POST",
    body: payload,
  });
}

export function transferStock(payload: StockTransfer) {
  return apiRequest<{
    out: MovementPublic;
    incoming: MovementPublic;
    from_balance: BalancePublic;
    to_balance: BalancePublic;
  }>("/api/v1/stock/transfers", {
    method: "POST",
    body: payload,
  });
}

export function checkIntegrity(warehouseId: string, variantId: string): Promise<IntegrityCheck> {
  return apiRequest<IntegrityCheck>(
    `/api/v1/stock/integrity?warehouse_id=${warehouseId}&variant_id=${variantId}`,
  );
}

export function rebuildBalance(warehouseId: string, variantId: string): Promise<BalancePublic> {
  return apiRequest<BalancePublic>(
    `/api/v1/stock/rebuild?warehouse_id=${warehouseId}&variant_id=${variantId}`,
    { method: "POST" },
  );
}

